import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { bp } from '../../../styles/breakPoints.tsx';
import ResizedImage from '../ResizedImage.tsx';
import { Color, Image } from '../../types.ts';
import { Icon } from '@hubspot/cms-components';
interface BoxContent {
  content: {
    title: string;
    title_image: Image;
    content: string;
  }
  color: {
    background_color: Color;
    text_color: Color;
  }
  button: {
    text: string;
    icon: {
      name: string;
      unicode: string;
      type: string;
      icon_set: string;
    };
    link: string;
    style: string;
    position: 'left' | 'right' | 'center';
  }
}
interface BoxesProps {
  fieldValues: {
    background_image: Image;
    background_image_mobile: Image;
    video_src: string;
    top_right_box: BoxContent;
    top_left_box: BoxContent;
    bottom_left_box: BoxContent;
    bottom_right_box: BoxContent;
  }
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 12px;

  @media ${bp.lg} {
    grid-template-columns: repeat(2, minmax(300px, 1fr));
    grid-gap: 24px;
  }

  @media ${bp.lg} {
    max-height: 700px;
  }

  .box {
    border-radius: 16px;
    padding: 24px 16px;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    position: relative;

    @media ${bp.md} {
      padding: 48px 24px 24px 24px;
    }

    &.bottom {
      padding: 24px 16px;

      @media ${bp.md} {
        padding: 48px;
      }
    }
  }

  .image-container {
    display: flex;
    border-radius: 16px;
    overflow: hidden;
    position: relative;

    max-height: 500px;

    @media ${bp.lg} {
      max-height: initial;
    }

    img, video {
      max-width: 100%;
      object-fit: cover;
      aspect-ratio: 1/1;
      min-height: 400px;
      width: 100%;
    }

    .text-content {
      position: absolute;
      background: var(--Pvrit-Lumme, #004107);
      bottom: 0;
      margin-bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      width: 90%;

      button {
        width: 100%;

        @media ${bp.xs} {
          width: auto;
        }
      }
    }
  }

  .right-side {
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media ${bp.md} {
      display: grid;
      gap: 24px; /* Space between grid items */
      grid-template-columns: 1fr 1fr; /* Two equal columns */
      grid-template-rows: auto auto; /* Two rows */
    }

    .item1 {
      grid-column: 1; /* First row, first column */
      grid-row: 1;
    }

    .item2 {
      grid-column: 2; /* First row, second column */
      grid-row: 1;
    }

    .item3 {
      grid-column: 1 / span 2; /* Second row, spanning across two columns */
      grid-row: 2;

      button {
        width: 100%;

        @media ${bp.xs} {
          width: auto;
        }
      }

    }
  }

  .image-container, .right-side {
    @media ${bp.lg} {
      max-height: 700px;
    }
  }

  .title-container {
    display: flex;
    align-items: center;
    gap: 20px;

    h3 {
      font-size: 20px;
      margin: 0;
      line-height: 1.2;

      @media ${bp.md} {
        font-size: 24px;
      }
    }
  }

  .button-container {
    position: absolute;
    bottom: 24px;
    width: calc(100% - 32px);
    display: flex;

    @media ${bp.md} {
      width: calc(100% - 48px);
    }
  }

  .box-text-content {
    margin-bottom: 44px;
  }

`;


function Boxes(props: BoxesProps) {

  const {
    background_image, video_src, background_image_mobile,
    top_right_box, top_left_box, bottom_left_box, bottom_right_box
  } = props.fieldValues;

  const [isMobile, setIsMobile] = useState(false);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      const imageContainer = document.getElementById('boxes-image-container');
      if (imageContainer) {
        setImageWidth(imageContainer.clientWidth);
        setImageHeight(imageContainer.clientHeight);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderBoxContent = (boxName: string, box: BoxContent, additionalClasses: string) => {
    const buttonPosition = box.button.position === 'left' ? 'flex-start' : box.button.position === 'right' ? 'flex-end' : 'center';

    return (
      <div
        className={"box " + additionalClasses}
        style={{ backgroundColor: box.color.background_color.css, color: box.color.text_color.css }}>
        <div className="title-container">
          {box.content.title_image.src && (
            <ResizedImage
              src={box.content.title_image.src}
              alt={box.content.title_image.alt}
              width={50} height={50} />
          )}
          <h3 dangerouslySetInnerHTML={{ __html: box.content.title }}></h3>
        </div>
        <div className='box-text-content' dangerouslySetInnerHTML={{ __html: box.content.content }}></div>
        {(box.button.text || box.button.icon.name) && <div className="button-container" style={{ justifyContent: buttonPosition }}>
          {box.button.link && <a href={box.button.link}>
              <button type="button" className={'lumme-button ' + box.button.style}>
                {box.button.text}
                {box.button.icon.name && (
                  <Icon fieldPath={boxName + ".button.icon"} height={16} />
                )}
              </button>
            </a>
          }
          {!box.button.link && <button type="button" className={'lumme-button ' + box.button.style}>
            {box.button.text}
            {box.button.icon.name && (
              <Icon fieldPath={boxName + ".button.icon"} height={16} />
            )}
          </button>}
        </div>}
      </div>
    );
  }

  return (
    <Grid>
      <div className='image-container' id="boxes-image-container">
        {video_src
          ? <video src={video_src} autoPlay loop muted playsInline />
          : <ResizedImage
            src={isMobile ? background_image_mobile.src : background_image.src}
            alt={isMobile ? background_image_mobile.alt : background_image.alt}
            width={imageWidth} height={imageHeight} loading='lazy' />
        }
        {renderBoxContent('bottom_left_box', bottom_left_box, 'text-content')}
      </div>
      <div className='right-side'>
        {renderBoxContent('top_left_box', top_left_box, 'item1')}
        {renderBoxContent('top_right_box', top_right_box, 'item2')}
        {renderBoxContent('bottom_right_box', bottom_right_box, 'item3')}
      </div>
    </Grid>
  );
}

export default Boxes;
